import { meetingBrands } from '@omni/services/brand/meeting-brands';
import { Injectable, Injector, inject } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { AlertService } from '@omni/services/alert/alert.service';
import { NotificationService, ToastStyle } from '@omni/services/notification/notification.service';
import { VoiceNoteCaptureResult, VoiceNoteService } from './voice-note.service';
import { DetectedMentions } from '@omni/ai/features/detected-mentions';
import { VOICE_NOTE_CAPTURE_MODAL_CLASS_NAME } from '@omni/config/shared.config';
import { AccountOfflineService } from '@omni/services/account/account.offline.service';
import { BrandOfflineService } from '@omni/services/brand/brand.service';
import { ContactOfflineService } from '@omni/services/contact/contact.service';
import { DeviceService } from '@omni/services/device/device.service';
import { UIService } from '@omni/services/ui/ui.service';
import { GoAppNavigationService } from '@omni/services/go-app-navigation.service';
import { VoiceNote, VoiceNoteType } from '@omni/classes/voice-note/voice-note.class';
import { detectionForReview, knownCustomerOf } from './note-dictation-seed';
import { filter, firstValueFrom, race, take, timer } from 'rxjs';
import {
  buildDictationCollections,
  canMatchCustomers,
  CapturedLocation,
  DictationCollections,
  ConfirmedDictation,
  KnownCustomer,
  resolveDictation,
  TargetMeeting,
} from './dictation-resolution';

/** How long a dictation waits for a running sync to load the rep's contacts before the review opens without them. */
export const CONTACTS_WAIT_MS = 60000;

/** Whether the review is confirming details for a brand-new meeting or merging into an open one. */
export type DictationReviewMode = 'create' | 'merge';

/** What the review needs beyond the detected mentions themselves. */
export interface DictationReviewContext {
  /**
   * The meeting the confirmed dictation will be applied to. Its presence is what
   * makes the review a merge: deriving the wording from the same value the write
   * uses is what stops the review offering to create a meeting that already exists.
   */
  targetMeeting?: TargetMeeting | null;
  /** Where the note was captured, when the capturing device recorded one. */
  capturedLocation?: CapturedLocation | null;
  /** A customer the note already names, shown alongside the detected ones. */
  known?: KnownCustomer | null;
  /** What was said, so the review can show the words its reading was made from. */
  transcript?: string | null;
}

export interface NewMeetingDictation {
  confirmed: ConfirmedDictation;
  transcript: string;
  /** Set when the capture was saved as a note because detection gave nothing. */
  sourceNote?: VoiceNote;
}

/**
 * The one Dictate step every entry point shares: resolve the detected mentions
 * against the rep's offline records and open the structured review. The
 * existing-meeting, new-activity and My Notes entries all route through here.
 */
@Injectable({ providedIn: 'root' })
export class DictationFlowService {
  private readonly modalCtrl = inject(ModalController);
  private readonly contactService = inject(ContactOfflineService);
  private readonly accountService = inject(AccountOfflineService);
  private readonly brandService = inject(BrandOfflineService);
  private readonly alertService = inject(AlertService);
  private readonly translate = inject(TranslateService);
  private readonly voiceNoteService = inject(VoiceNoteService);
  private readonly notificationService = inject(NotificationService);
  private readonly deviceService = inject(DeviceService);
  private readonly uiService = inject(UIService);
  private readonly injector = inject(Injector);

  /**
   * The records a dictation is matched against.
   *
   * The contact list is filled by the sync. While a sync is running, the review
   * waits for it to load the contacts (see {@link untilContactsLoaded}); with no
   * sync running and no contacts in memory, they are read from the device, which
   * needs no connection.
   */
  private async loadedCollections(): Promise<DictationCollections> {
    if (!this.contactService.contacts?.length) {
      if (this.deviceService.syncInProgress) {
        await this.untilContactsLoaded();
      } else {
        await this.contactService
          .fetchContactsForConfiguredDisplay(false, true)
          .catch((error) => console.error('DictationFlowService: loading contacts failed', error));
      }
    }
    return buildDictationCollections(
      this.contactService.contacts,
      this.accountService.accounts,
      meetingBrands(this.brandService.brands),
    );
  }

  /**
   * Waits under a loader until the running sync has loaded the rep's contacts,
   * the sync ends, or `CONTACTS_WAIT_MS` passes, whichever comes first. A device's
   * first sync after an install can take minutes, so the wait is bounded: past it
   * the review opens without contacts and says they are still loading.
   */
  private async untilContactsLoaded(): Promise<void> {
    await this.uiService.displayLoader({
      message: this.translate.instant('LOADING_CONTACTS'),
      duration: CONTACTS_WAIT_MS,
    });
    try {
      await firstValueFrom(race(
        this.contactService.contactsReady$.pipe(filter(Boolean), take(1)),
        this.deviceService.syncInProgress$.pipe(filter((inProgress) => !inProgress), take(1)),
        timer(CONTACTS_WAIT_MS),
      ));
    } finally {
      await this.uiService.dismissLoader();
    }
  }

  /** Null on cancel, and on Save for Later, which has already opened the note in My Notes. */
  async dictateNewMeeting(
    formatId?: string,
    formatName?: string,
    known?: KnownCustomer | null,
  ): Promise<NewMeetingDictation | null> {
    const { VoiceNoteCaptureComponent } = await import('@omni/components/voice-notes/voice-note-capture/voice-note-capture');
    const modal = await this.modalCtrl.create({
      component: VoiceNoteCaptureComponent,
      componentProps: { type: VoiceNoteType.CallNote, primaryAction: 'create-meeting', formatId, formatName },
      backdropDismiss: false,
      cssClass: VOICE_NOTE_CAPTURE_MODAL_CLASS_NAME,
    });
    await modal.present();
    const { data, role } = await modal.onDidDismiss<VoiceNoteCaptureResult>();
    if (!data || (role !== 'save' && role !== 'create-meeting')) {
      return null;
    }
    if (role !== 'create-meeting') {
      const savedNote = await this.voiceNoteService.saveCapture(data);
      this.voiceNoteService.queueNoteToOpen(savedNote);
      this.injector.get(GoAppNavigationService).navigateToMenuOption({ component: 'VoiceNotesPageComponent' });
      return null;
    }

    // Without a detector's reading the review opens on the capture saved as a note.
    const note = data.detected ? null : await this.voiceNoteService.saveCapture(data);
    if (data.detectionFailed) {
      this.notificationService.notify(
        this.translate.instant('DICTATE_DETECTION_FAILED_CREATE'), 'Dictation', 'top', ToastStyle.INFO,
      );
    }
    let targetMeeting: TargetMeeting | null = null;
    if (note) {
      const { VoiceNoteMeetingService } = await import('./voice-note-meeting.service');
      targetMeeting = this.injector.get(VoiceNoteMeetingService).targetMeetingFor(note);
    }
    const confirmed = await this.reviewAndConfirm(detectionForReview(data.detected, note), {
      targetMeeting,
      known: known ?? knownCustomerOf(note),
      transcript: data.transcript,
    });
    if (!confirmed) {
      // Only a capture that never became a note is lost by backing out.
      if (!note) await this.offerToKeepCapture(data);
      return null;
    }
    return { confirmed, transcript: data.transcript, sourceNote: note ?? undefined };
  }

  /**
   * Offer to keep a dictation the rep backed out of reviewing. A capture that never
   * became a note exists nowhere but this screen, so letting Cancel discard it
   * silently loses what was said; the rep is asked rather than having a note saved
   * on their behalf.
   */
  async offerToKeepCapture(data: VoiceNoteCaptureResult): Promise<void> {
    if (!data?.transcript?.trim()) {
      return;
    }
    const answer = await this.alertService.showAlert(
      { message: this.translate.instant('DICTATE_KEEP_ON_CANCEL') },
      this.translate.instant('SAVE'),
      this.translate.instant('DISCARD'),
    );
    if (answer?.role !== 'ok') {
      return;
    }
    try {
      await this.voiceNoteService.saveCapture(data);
      this.notificationService.notify(
        this.translate.instant('DICTATE_SAVED_TO_MY_NOTES'), 'Dictation', 'top', ToastStyle.INFO,
      );
    } catch (error) {
      console.error('DictationFlowService: keeping the cancelled capture failed', error);
      this.notificationService.notify(
        this.translate.instant('SOMETHING_REALLY_BAD_HAPPENED'), 'Dictation', 'top', ToastStyle.DANGER,
      );
    }
  }

  /**
   * Present the structured review for the detected mentions and return what the
   * rep confirmed, or null if they backed out. Collections are projected from the
   * rep's own contacts, accounts and brands so every entry point resolves the
   * same way. A customer the source note already names is shown alongside the
   * detected ones, since it lands on the meeting just as they do.
   */
  async reviewAndConfirm(
    detected: DetectedMentions,
    context: DictationReviewContext = {},
  ): Promise<ConfirmedDictation | null> {
    const { targetMeeting, capturedLocation, known, transcript } = context;
    const mode: DictationReviewMode = targetMeeting ? 'merge' : 'create';
    const { VoiceNoteReviewComponent } = await import(
      '@omni/components/voice-notes/voice-note-review/voice-note-review'
    );
    const collections = await this.loadedCollections();
    if (!canMatchCustomers(collections)) {
      this.notificationService.notify(
        this.translate.instant('DICTATE_CUSTOMERS_NOT_READY'),
        'Dictate',
        'top',
        ToastStyle.DANGER,
      );
    }
    const resolved = resolveDictation(detected, collections, known);
    const review = await this.modalCtrl.create({
      component: VoiceNoteReviewComponent,
      componentProps: { mode, resolved, capturedLocation, targetMeeting, transcript },
      backdropDismiss: false,
      cssClass: VOICE_NOTE_CAPTURE_MODAL_CLASS_NAME,
    });
    await review.present();
    const { data, role } = await review.onDidDismiss();
    return data && role === 'confirm' ? (data as ConfirmedDictation) : null;
  }
}
